/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useState } from 'react'
import { View, Text } from '@components/atoms'
import { Button, Chip, Select, MenuItem } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'

import {
	SeasonProductAddType,
	SeasonProductBatchUpdateType,
	SeasonProductType,
	ProductType,
} from '@schemas'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

/**
 * 판매 방식. 상시판매와 프리오더는 **직교한다** — 상시판매는 "수량 제한이 있는가",
 * 프리오더는 "언제 받는가"다. 그래서 네 조합이 모두 실재한다 (CATALOG_PLAN §7.2).
 *
 * `alwaysAvailableYn` 이 실제로 가르는 것은 **수량 제한뿐**이다:
 *   0 → 시즌상품의 QUANTITY 가 소진되면 품절
 *   1 → 수량을 보지 않는다. 품절은 상품의 SOLDOUT_YN 을 수동으로 올릴 때만
 * 시즌 기간(not_start·expired)은 **양쪽 모두에 걸린다** — 상시판매도 시즌 밖에서는 안 팔린다.
 */
const getSaleMode = (
	alwaysAvailableYn: number,
	preorderYn: number,
): { label: string; color: 'default' | 'info' | 'warning' } => {
	if (alwaysAvailableYn === 1) {
		return preorderYn === 1
			? { label: '상시 프리오더', color: 'warning' }
			: { label: '상시 판매', color: 'info' }
	}
	return preorderYn === 1
		? { label: '한정 프리오더', color: 'warning' }
		: { label: '수량 한정', color: 'default' }
}

/**
 * 서버가 판정한 판매 상태(SeasonProduct.status). 전에는 'active' 가 아니면 전부 Inactive 로 찍어서
 * 얼리버드 시작 전인 상품(preorder_not_started)이 비활성처럼 보였다.
 */
const getSaleStatus = (
	status: string,
): { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' } => {
	switch (status) {
		case 'active':
			return { label: '판매 중', color: 'success' }
		case 'not_start':
			return { label: '시즌 시작 전', color: 'default' }
		case 'expired':
			return { label: '시즌 종료', color: 'default' }
		case 'inactive':
			return { label: '제품 미사용', color: 'default' }
		case 'preorder_not_started':
			return { label: '단계 시작 전', color: 'info' }
		case 'preorder_closed':
			return { label: '프리오더 완판', color: 'error' }
		case 'sold_out':
			return { label: '품절', color: 'warning' }
		default:
			return { label: status, color: 'default' }
	}
}

/** 가격표 문법 — 딱 떨어지면 `.00` 없이 (PRICING §3) */
const usd = (value: number): string =>
	value.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
		maximumFractionDigits: 2,
	})

const krw = (value: number): string =>
	value.toLocaleString('ko-KR', { style: 'currency', currency: 'KRW' })

interface SeasonProductListProps {
	products: ProductType[]
	seasonProducts: SeasonProductType[]
	/** 제품 추가만 여기서 한다. 값·단계 수정은 전부 상세 화면에서 */
	onUpdateSeasonProduct: (
		updatedSeasonProduct: SeasonProductBatchUpdateType[],
		deletedSeasonProduct: SeasonProductType[],
	) => void
	/** 상세(전체 화면)로 보낸다 — 시즌 상품의 모든 수정이 거기서 일어난다 */
	onDetailClick: (seasonProduct: SeasonProductType) => void
}

/**
 * 시즌 세부 정보의 제품 리스트. **읽기 전용.** 여기서 고치는 것은 없고, 카드의 `수정` 이
 * 상세 화면으로 보낸다(2026-09-16). 유일한 쓰기는 "제품 추가" — 제품을 고르면 제품 정가를
 * 시즌단가로 놓고 시즌에 넣는다. 나머지 값은 상세에서 잡는다.
 *
 * 표가 아니라 카드다. 열이 12개가 되면서 가로 스크롤이 생겼고, 숫자를 한 줄에 늘어놓으면
 * 무엇이 값이고 무엇이 실적인지 훑어지지 않았다. 상품 하나 = 카드 하나, 안에서
 * 값(시즌단가·할인 전·기준)과 실적(매출·수량)을 stat tile 로 나눈다 — 시즌 정보 패널의
 * 실적 타일과 같은 문법. 큰 숫자는 가늘게, 라벨은 작고 굵게 (DESIGN_SYSTEM §0).
 */
export const SeasonProductList: React.FC<SeasonProductListProps> = ({
	products,
	seasonProducts,
	onUpdateSeasonProduct,
	onDetailClick,
}) => {
	// state
	const [isAdding, setIsAdding] = useState<boolean>(false)
	const [addProductId, setAddProductId] = useState<string>('')

	// 이미 이 시즌에 있는 제품은 고를 수 없다 — 같은 제품이 두 번 서면 판정이 갈린다
	const addableProducts: ProductType[] = products.filter(
		aProduct => !seasonProducts.some(aSeason => aSeason.productId === aProduct.id),
	)

	const handleAdd = () => {
		const product = products.find(aProduct => aProduct.id === addProductId)
		if (product === undefined) return

		const payload: SeasonProductAddType = {
			iudFlag: 'I',
			seasonId: '',
			productId: product.id,
			usd: product.usd,
			krw: product.krw,
			discountUsd: 0,
			quantity: 0,
			activeYn: 1,
			preorderYn: 0,
		}
		onUpdateSeasonProduct([payload], [])
		setIsAdding(false)
		setAddProductId('')
	}

	return (
		<View css={style}>
			<View className="panelView">
				<View className="panelHead">
					<Text category="s1" className="panelTitle">
						제품 리스트
					</Text>
					<Text category="p2" className="panelHint">
						{`${seasonProducts.length}개`}
					</Text>
					<View className="panelSpacer" />
					{!isAdding && (
						<Button
							size="small"
							variant="outlined"
							startIcon={<AddIcon />}
							onClick={() => setIsAdding(true)}
							disabled={addableProducts.length === 0}
							className="headButton"
						>
							제품 추가
						</Button>
					)}
				</View>

				{isAdding && (
					<View className="addRow">
						<Select
							value={addProductId}
							onChange={e => setAddProductId(String(e.target.value))}
							size="small"
							displayEmpty
							className="addSelect"
						>
							<MenuItem value="" disabled>
								제품 선택
							</MenuItem>
							{addableProducts.map(aProduct => (
								<MenuItem key={aProduct.id} value={aProduct.id}>
									{aProduct.name}
								</MenuItem>
							))}
						</Select>
						<Text category="p2" className="addHint">
							제품 정가가 시즌단가로 들어간다. 값·수량·단계는 추가 뒤 수정에서.
						</Text>
						<View className="panelSpacer" />
						<Button
							size="small"
							variant="text"
							onClick={() => {
								setIsAdding(false)
								setAddProductId('')
							}}
						>
							취소
						</Button>
						<Button
							size="small"
							variant="contained"
							onClick={handleAdd}
							disabled={addProductId === ''}
						>
							추가
						</Button>
					</View>
				)}

				<View className="cardList">
					{seasonProducts.map(product => {
						const saleMode = getSaleMode(product.alwaysAvailableYn, product.preorderYn)
						const saleStatus = getSaleStatus(product.status)
						const progress =
							product.seasonQuantity > 0
								? Math.min(100, (product.soldQuantity / product.seasonQuantity) * 100)
								: 0

						return (
							<View key={product.id} className="productCard">
								{/* ── 머리: 무엇인지 · 어떤 상태인지 · 동작 ───────────── */}
								<View className="cardHead">
									<View className="cardIdentity">
										<Text category="s1" className="productName">
											{product.productName}
										</Text>
										<Text category="p2" className="productCode">
											{product.productCode}
										</Text>
									</View>
									<View className="cardChips">
										<Chip label={saleMode.label} size="small" variant="outlined" />
										<Chip
											label={saleStatus.label}
											size="small"
											variant={product.status === 'active' ? 'filled' : 'outlined'}
										/>
										{product.seasonActiveYn === 0 && (
											<Chip label="품절처리" size="small" variant="outlined" />
										)}
										{/*
											전체 화면 상세로 간다. 값·수량·판매 단계·이행 로드맵 전부
											거기서 고친다 (CATALOG_PLAN §7.4).
										*/}
										<Button
											size="small"
											variant="outlined"
											onClick={() => onDetailClick(product)}
											className="detailButton"
										>
											수정
										</Button>
									</View>
								</View>

								{/* ── 몸통: 값과 실적 ────────────────────────────────── */}
								<View className="statGrid">
									<View className="stat">
										<Text category="p2" className="statLabel">
											시즌단가
										</Text>
										<Text className="statValue">{usd(product.seasonUsd)}</Text>
										<Text category="p2" className="statSub">
											{krw(product.seasonKrw)}
										</Text>
									</View>
									<View className="stat">
										<Text category="p2" className="statLabel">
											할인 전 가격
										</Text>
										<Text className="statValue">
											{product.seasonDiscountUsd > 0
												? usd(product.seasonDiscountUsd)
												: '—'}
										</Text>
										<Text category="p2" className="statSub">
											{product.seasonDiscountUsd > product.seasonUsd
												? 'shop 에 줄 긋는 값'
												: '표시 안 함'}
										</Text>
									</View>
									<View className="stat">
										<Text category="p2" className="statLabel">
											기준단가
										</Text>
										<Text className="statValue dim">{usd(product.standardUsd)}</Text>
										<Text category="p2" className="statSub">
											제품 정가 · 참고
										</Text>
									</View>
									<View className="stat">
										<Text category="p2" className="statLabel">
											매출
										</Text>
										<Text className="statValue">{usd(product.soldUsd)}</Text>
										<Text category="p2" className="statSub">
											{product.alwaysAvailableYn === 1
												? `${product.soldQuantity.toLocaleString()} 대 · 수량 제한 없음`
												: `${product.soldQuantity.toLocaleString()} / ${product.seasonQuantity.toLocaleString()} 대`}
										</Text>
										{product.alwaysAvailableYn === 0 && (
											<View className="meter">
												<View className="meterFill" style={{ width: `${progress}%` }} />
											</View>
										)}
									</View>
								</View>
							</View>
						)
					})}

					{seasonProducts.length === 0 && (
						<View className="emptyView">
							<Text category="p2">이 시즌에 올린 제품이 없다.</Text>
						</View>
					)}
				</View>
			</View>
		</View>
	)
}

const style = css({
	...boShared,
	display: 'flex',
	flexDirection: 'column',

	p: {
		margin: 0,
		textAlign: 'left',
		fontFamily: 'inherit',
	},

	// 시즌 정보·실적과 같은 패널 문법 — 키커 한 줄, 그 아래 내용. 박스 없음
	'.panelView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		padding: '20px 0 24px',
		borderRadius: 0,
		backgroundColor: 'transparent',
	},
	'.panelHead': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		minHeight: 30,
	},
	'.panelTitle': {
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		width: 'auto',
	},
	'.panelHint': {
		fontSize: 12,
		fontWeight: 300,
		color: mono.faint,
		fontVariantNumeric: 'tabular-nums',
	},
	'.panelSpacer': {
		flex: 1,
	},
	'.headButton': {},

	'.addRow': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '8px 12px',
		padding: '14px 0',
		borderRadius: 0,
		borderTop: `1px solid ${mono.line}`,
		borderBottom: `1px solid ${mono.line}`,
		backgroundColor: 'transparent',

		'.addSelect': {
			minWidth: '14rem',
		},
		'.addHint': {
			fontSize: 12.5,
			fontWeight: 300,
			color: mono.dim,
		},
	},

	// 제품은 테두리를 두르지 않는다 — 헤어라인 하나로만 나눈다
	'.cardList': {
		display: 'flex',
		flexDirection: 'column',
	},

	'.productCard': {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: '20px 0 24px',
		borderTop: `1px solid ${mono.line}`,

		'&:last-of-type': {
			paddingBottom: 8,
		},
	},

	'.cardHead': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '8px 16px',
	},
	'.cardIdentity': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
		gap: 10,
		minWidth: '12rem',
		flex: 1,

		'.productName': {
			fontSize: 17,
			fontWeight: 400,
			color: mono.ink,
			width: 'auto',
		},
		'.productCode': {
			fontSize: 11,
			fontWeight: 500,
			color: mono.faint,
			textTransform: 'uppercase',
			letterSpacing: '.14em',
			width: 'auto',
		},
	},
	'.cardChips': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
		marginLeft: 'auto',

		'.detailButton': {
			marginLeft: 8,
			whiteSpace: 'nowrap',
		},
	},

	// 값·실적 타일 — 좁아지면 칸 수가 줄고, 가로 스크롤은 생기지 않는다
	'.statGrid': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))',
		gap: '12px 24px',
	},
	'.stat': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		minWidth: 0,

		'.statLabel': {
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.14em',
			textTransform: 'uppercase',
			color: mono.faint,
		},
		'.statValue': {
			fontSize: 24,
			lineHeight: 1,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums',
			whiteSpace: 'nowrap',

			'&.dim': {
				color: mono.faint,
			},
		},
		'.statSub': {
			fontSize: 12,
			fontWeight: 300,
			color: mono.dim,
			fontVariantNumeric: 'tabular-nums',
		},
	},
	'.meter': {
		height: 2,
		marginTop: 6,
		borderRadius: 0,
		backgroundColor: mono.line,
		overflow: 'hidden',
	},
	'.meterFill': {
		height: '100%',
		backgroundColor: mono.ink,
	},

	'.emptyView': {
		padding: '32px 0',
		color: mono.faint,
		textAlign: 'center',
		p: { textAlign: 'center', fontWeight: 300, color: mono.faint },
	},
})
